import { NextResponse } from "next/server";

/**
 * Canonical wire shape for the auth-related 401 responses the API
 * surface returns. Every emitter (proxy, requireAuth, /api/auth/refresh)
 * and every parser (browser fetch helpers, WingmanClient) imports from
 * here so the shape is single-sourced.
 */

export const SESSION_EXPIRED_MESSAGE =
  "Your session has expired. Please log in again.";
export const NOT_AUTHENTICATED_MESSAGE = "No active session. Please log in.";

export type AuthErrorCode =
  | "session_expired"
  | "not_authenticated"
  | "refresh_required";

export interface AuthErrorBody {
  error: AuthErrorCode;
  message: string;
}

export function sessionExpiredResponse(): NextResponse {
  return NextResponse.json<AuthErrorBody>(
    { error: "session_expired", message: SESSION_EXPIRED_MESSAGE },
    { status: 401 },
  );
}

export function notAuthenticatedResponse(): NextResponse {
  return NextResponse.json<AuthErrorBody>(
    { error: "not_authenticated", message: NOT_AUTHENTICATED_MESSAGE },
    { status: 401 },
  );
}

/** Recoverable before dispatch; this is not a terminal session failure. */
export function refreshRequiredResponse(): NextResponse {
  return NextResponse.json<AuthErrorBody>(
    {
      error: "refresh_required",
      message: "Session refresh required.",
    },
    { status: 401, headers: { "Cache-Control": "no-store" } },
  );
}

/**
 * Inspects a 401 response body for the canonical auth-error shape.
 * Returns the matched code, or `null` if the body is not a recognised
 * auth-error body (or not JSON at all).
 *
 * Accepts either the parsed body or the raw response text — the parser
 * runs `JSON.parse` itself and tolerates non-JSON input.
 */
export function classifyAuthErrorBody(
  bodyOrText: unknown,
): AuthErrorCode | null {
  let body: unknown = bodyOrText;
  if (typeof bodyOrText === "string") {
    if (!bodyOrText) return null;
    try {
      body = JSON.parse(bodyOrText);
    } catch {
      return null;
    }
  }

  if (typeof body !== "object" || body === null) return null;
  const code = (body as { error?: unknown }).error;
  if (
    code === "session_expired" ||
    code === "not_authenticated" ||
    code === "refresh_required"
  ) return code;
  return null;
}
